import * as React from 'react';
import styled, { css } from 'styled-components';

import { Timestamp, Spinner } from 'components/common';
import type {
  Evidence,
  EventEvidence,
  LogEvidence,
} from 'security-app/components/Investigations/Details/TimelineSegment/types';
import useMessage from 'views/hooks/useMessage';
import Popover from 'components/common/Popover';
import {
  isLogEvidence,
  removeIlluminateEventDefinitionPrefix,
  evidenceTypeLabel,
} from 'security-app/components/Investigations/Details/TimelineSegment/Utils';
import MessageSummary from 'search/MessageSummary';

const HoverLabelContainer = styled.div<{ positionX: number; positionY: number }>(
  ({ positionX, positionY }) => css`
    position: absolute;
    left: ${positionX}px;
    top: ${positionY + 10}px;
  `,
);

const StyledTimestamp = styled(Timestamp)(
  ({ theme }) => css`
    color: ${theme.colors.text.secondary};
  `,
);

const Header = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  margin-bottom: 5px;
`;

const HoverLabelFoundation = ({
  evidence,
  children = undefined,
  isLoading = false,
}: React.PropsWithChildren<{ evidence: Evidence; isLoading?: boolean }>) => {
  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div>
      <Header>
        <b>{evidenceTypeLabel(evidence.type)}</b>
        <StyledTimestamp dateTime={evidence.timestamp} />
      </Header>
      {children}
    </div>
  );
};

const EventHoverLabel = ({ evidence }: { evidence: EventEvidence }) => (
  <HoverLabelFoundation evidence={evidence}>
    {removeIlluminateEventDefinitionPrefix(evidence.entity.message)}
  </HoverLabelFoundation>
);

const MessageHoverLabel = ({ evidence }: { evidence: LogEvidence }) => {
  const { data: message, isInitialLoading } = useMessage(evidence.entity.index, evidence.id);

  return (
    <HoverLabelFoundation evidence={evidence} isLoading={isInitialLoading}>
      <MessageSummary messageFields={message?.fields} renderMessageRow={() => message?.fields.message} />
    </HoverLabelFoundation>
  );
};

const TimelineHoverlabel = ({
  positionX,
  positionY,
  evidence,
}: {
  positionX: number;
  positionY: number;
  evidence: Evidence;
}) => (
  <HoverLabelContainer positionX={positionX} positionY={positionY}>
    <Popover opened width={300} withArrow withinPortal>
      <Popover.Target>
        <div style={{ width: '20px' }} />
      </Popover.Target>
      <Popover.Dropdown id="edit-search-filter-dropdown">
        {isLogEvidence(evidence) ? <MessageHoverLabel evidence={evidence} /> : <EventHoverLabel evidence={evidence} />}
      </Popover.Dropdown>
    </Popover>
  </HoverLabelContainer>
);

export default TimelineHoverlabel;
